import { Wrench, Zap, Twitter, Instagram, Linkedin } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export default function Footer() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className={`relative border-t overflow-hidden ${isDark ? "border-[#222226]" : "border-slate-200 bg-white"}`}>
      {/* Final CTA block */}
      <div className="py-20 px-6 md:px-12 lg:px-20 relative">
        {/* Glow */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 blur-[80px] pointer-events-none ${isDark ? "bg-[#00F0FF]/8" : "bg-brand-orange/8"}`} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className={`text-xs uppercase tracking-[0.2em] mb-4 font-mono font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
            Ready?
          </p>
          <h2 className={`font-display font-black text-5xl lg:text-7xl tracking-tighter mb-8 leading-none ${isDark ? "text-white" : "text-slate-900"}`}>
            Book Your Mechanic
            <br />
            <span
              className={isDark ? "text-[#00F0FF]" : "text-brand-orange"}
              style={{ textShadow: isDark ? "0 0 60px rgba(0,240,255,0.4)" : "0 0 60px rgba(249,115,22,0.3)" }}
            >
              Right Now.
            </span>
          </h2>
          <button
            data-testid="footer-book-now-button"
            onClick={() => scrollTo("#booking")}
            className={`btn-magnetic haptic inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all ${isDark ? "bg-[#00F0FF] text-black shadow-[0_0_48px_rgba(0,240,255,0.5)] hover:bg-white hover:shadow-[0_0_64px_rgba(255,255,255,0.3)]" : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_8px_32px_rgba(249,115,22,0.4)] hover:shadow-[0_12px_48px_rgba(249,115,22,0.5)]"}`}
          >
            <Zap size={18} />
            Get Started — Free Quote
          </button>
          <p className={`text-xs mt-4 ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
            No commitment. Fixed pricing. Cancel anytime.
          </p>
        </div>
      </div>

      {/* Footer bottom */}
      <div className={`border-t px-6 md:px-12 lg:px-20 py-8 ${isDark ? "border-[#222226]" : "border-slate-200"}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isDark ? "bg-[#00F0FF]/10 border border-[#00F0FF]/20" : "bg-gradient-to-br from-brand-blue to-brand-blue-light"}`}>
              <Wrench size={14} className={isDark ? "text-[#00F0FF]" : "text-white"} />
            </div>
            <span className={`font-display font-bold text-sm ${isDark ? "text-white" : "text-slate-900"}`}>
              Wrench<span className={isDark ? "text-[#00F0FF]" : "text-brand-orange"}>OnWheels</span>
            </span>
          </div>

          {/* Links */}
          <div className={`flex items-center gap-6 text-xs ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
            {["Services", "How It Works", "FAQ"].map((l) => (
              <button
                key={l}
                onClick={() => scrollTo(`#${l.toLowerCase().replace(/ /g, "-")}`)}
                className={`transition-colors ${isDark ? "hover:text-white" : "hover:text-slate-900"}`}
              >
                {l}
              </button>
            ))}
            <span className={isDark ? "text-[#222226]" : "text-slate-300"}>|</span>
            <span className={`cursor-default ${isDark ? "hover:text-white" : "hover:text-slate-900"} transition-colors`}>Privacy</span>
            <span className={`cursor-default ${isDark ? "hover:text-white" : "hover:text-slate-900"} transition-colors`}>Terms</span>
          </div>

          {/* Social */}
          <div className="flex items-center gap-3">
            {[
              { Icon: Twitter, href: "#", label: "Twitter" },
              { Icon: Instagram, href: "#", label: "Instagram" },
              { Icon: Linkedin, href: "#", label: "LinkedIn" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                rel="noopener noreferrer"
                target="_blank"
                className={`w-8 h-8 rounded-md border flex items-center justify-center transition-all ${
                  isDark
                    ? "border-[#222226] text-[#8F9098] hover:border-[#00F0FF]/30 hover:text-[#00F0FF]"
                    : "border-slate-200 text-slate-400 hover:border-brand-blue/30 hover:text-brand-blue"
                }`}
              >
                <Icon size={13} />
              </a>
            ))}
          </div>
        </div>

        <div className={`max-w-7xl mx-auto mt-6 pt-6 border-t ${isDark ? "border-[#222226]" : "border-slate-200"}`}>
          <p className={`text-center text-xs ${isDark ? "text-[#8F9098]/60" : "text-slate-400"}`}>
            © 2026 WrenchOnWheels. All rights reserved. Not a licensed repair shop — mobile technician service.
          </p>
        </div>
      </div>
    </footer>
  );
}
